'use client';

/**
 * ViewTransitionWrapper - Next.js 16 View Transitions API Integration
 *
 * This component wraps content with React's experimental ViewTransition component
 * to enable smooth, native browser transitions between pages and UI states.
 *
 * @see https://nextjs.org/docs/app/api-reference/config/next-config-js/viewTransition
 * @see https://react.dev/reference/react/ViewTransition
 *
 * Usage:
 * <ViewTransitionWrapper>
 *   <YourPageContent />
 * </ViewTransitionWrapper>
 *
 * Note: This feature is experimental and requires:
 * - viewTransition: true in next.config.mjs experimental settings
 * - Browser support for View Transitions API (Chrome 111+, Edge 111+)
 * - Falls back gracefully in unsupported browsers
 */

import PropTypes from 'prop-types';
import { Suspense, unstable_ViewTransition as ViewTransition } from 'react';

/**
 * Check if ViewTransition is available (React canary feature)
 * Falls back gracefully if not available
 */
const hasViewTransition = typeof ViewTransition !== 'undefined';

/**
 * Loading fallback for suspense boundary
 */
const LoadingFallback = () => null;

/**
 * ViewTransitionWrapper component
 *
 * Wraps children with ViewTransition for smooth page transitions.
 * Automatically falls back to regular rendering if ViewTransition is unavailable.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to wrap with transitions
 * @param {string} [props.name] - Optional transition name for CSS targeting
 * @param {boolean} [props.enabled=true] - Whether to enable transitions
 */
const ViewTransitionWrapper = ({ children, name, enabled = true }) => {
  // If ViewTransition is not available or disabled, render children directly
  if (!hasViewTransition || !enabled) {
    return children;
  }

  // Wrap with ViewTransition for smooth page transitions
  return (
    <Suspense fallback={<LoadingFallback />}>
      <ViewTransition name={name}>{children}</ViewTransition>
    </Suspense>
  );
};

ViewTransitionWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.string,
  enabled: PropTypes.bool,
};

export default ViewTransitionWrapper;
